import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { getTenantId } from '@/repos/contentRepo';
import type { AuthSession, LoginCredentials, RegistrationData, AuthState, AuthContextType } from '@/types/auth';
import { createProfile, getUserProfile, getUserRole, upsertUserTenant, createSystemUser, getSystemUser, getSystemUserByEmail, testUsersTable } from '@/repos/userRepo';
import { checkAndCreateUsersTable } from '@/debug/checkAndCreateUsersTable';
import { cleanAuthUrl, clearLegacyTokenStorage, hasSensitiveAuthParameters } from '@/lib/authUrlSecurity';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_FALLBACK: AuthContextType = {
  user: null,
  session: null,
  loading: false,
  error: null,
  login: () => Promise.reject(new Error('Auth indisponível')),
  register: () => Promise.reject(new Error('Auth indisponível')),
  logout: () => Promise.resolve(),
  resetPassword: () => Promise.reject(new Error('Auth indisponível')),
  updatePassword: () => Promise.reject(new Error('Auth indisponível')),
  refreshSession: () => Promise.resolve(),
  hasRole: () => false,
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    console.error('❌ useAuth deve ser usado dentro de um AuthProvider');
    return AUTH_FALLBACK;
  }
  
  if (!context.login || !context.register || !context.logout) {
    console.error('❌ AuthContext incompleto:', context);
    return {
      ...AUTH_FALLBACK,
      ...context,
      login: context.login || AUTH_FALLBACK.login,
      register: context.register || AUTH_FALLBACK.register,
      logout: context.logout || AUTH_FALLBACK.logout,
    };
  }
  
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  
  const [state, setState] = useState<{
    user: any | null;
    session: AuthSession | null;
    loading: boolean;
    error: string | null;
  }>({
    user: null,
    session: null,
    loading: true,
    error: null,
  });

  // Estado para controlar se já foi feito redirecionamento inicial
  const hasInitialRedirectedRef = useRef(false);

  const markInitialRedirected = () => {
    hasInitialRedirectedRef.current = true;
  };

  // Once a session exists, sanitize token-like query/fragment values on every
  // SPA history transition as well as on the initial auth callback.
  useEffect(() => {
    if (!state.user) return undefined;
    const sanitize = () => {
      if (hasSensitiveAuthParameters()) cleanAuthUrl();
    };
    sanitize();
    window.addEventListener('hashchange', sanitize);
    window.addEventListener('popstate', sanitize);
    return () => {
      window.removeEventListener('hashchange', sanitize);
      window.removeEventListener('popstate', sanitize);
    };
  }, [state.user]);

  // Check for existing session on mount
  useEffect(() => {
    clearLegacyTokenStorage();
    const restoreSession = async () => {
      setState(prev => ({ ...prev, loading: true }));
      
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session && hasSensitiveAuthParameters()) cleanAuthUrl();
        
        if (session?.user) {
          // Apenas definir o usuário básico, sem carregar dados completos
          setState(prev => ({ 
            ...prev, 
            user: session.user, 
            loading: false 
          }));
          
          // Carregar dados completos em background (não bloquear a interface)
          setTimeout(async () => {
            try {
              const systemUser = await getSystemUser(session.user.id);
              if (systemUser) {
                const profile = await getUserProfile(systemUser.id);
                const userTenant = await getUserRole(systemUser.id);
                
                const authSession: AuthSession = {
                  user: {
                    id: session.user.id,
                    email: session.user.email!,
                    created_at: session.user.created_at,
                    updated_at: session.user.updated_at,
                  },
                  systemUser: systemUser,
                  profile: profile || await getUserProfile(systemUser.id),
                  userTenant: userTenant || await getUserRole(systemUser.id),
                  accessToken: session.access_token || '',
                  refreshToken: session.refresh_token || '',
                };
                
                setState(prev => ({ 
                  ...prev, 
                  session: authSession 
                }));
              }
            } catch (error) {
              console.error('Erro ao carregar dados do usuário em background:', error);
            }
          }, 100);
        } else {
          setState(prev => ({ ...prev, loading: false }));
        }
      } catch (error) {
        console.error('Erro ao restaurar sessão:', error);
        setState(prev => ({ ...prev, loading: false }));
      }
    };
    restoreSession();

    const { data: listener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        if (hasSensitiveAuthParameters()) cleanAuthUrl();
        // Primeiro definir o usuário básico para permitir redirecionamento
        setState(prev => ({ ...prev, user: session.user, loading: false }));
        
        const currentPath = window.location.pathname;
        const alreadyOnApp = currentPath === '/dashboard-home' || currentPath.startsWith('/dashboard');
        const shouldRedirect = !hasInitialRedirectedRef.current &&
          !alreadyOnApp &&
          (currentPath === '/login-screen' || currentPath === '/login' || currentPath === '/');
        
        if (shouldRedirect) {
          console.log('🧭 Redirecionando para dashboard-home (primeiro login)');
          navigate('/dashboard-home');
          markInitialRedirected();
        } else {
          console.log('🧭 Mantendo página atual:', currentPath);
        }
        
        // Carregar dados completos em background
        setTimeout(async () => {
          try {
            const systemUser = await getSystemUser(session.user.id);
            if (systemUser) {
              const profile = await getUserProfile(systemUser.id);
              const userTenant = await getUserRole(systemUser.id);
              
              const authSession: AuthSession = {
                user: {
                  id: session.user.id,
                  email: session.user.email!,
                  created_at: session.user.created_at,
                  updated_at: session.user.updated_at,
                },
                systemUser: systemUser,
                profile: profile || await getUserProfile(systemUser.id),
                userTenant: userTenant || await getUserRole(systemUser.id),
                accessToken: session.access_token || '',
                refreshToken: session.refresh_token || '',
              };
              
              setState(prev => ({ 
                ...prev, 
                session: authSession 
              }));
            }
          } catch (error) {
            console.error('Erro ao carregar dados do usuário no auth state change:', error);
          }
        }, 100);
      } else if (event === 'SIGNED_OUT') {
        setState({
          user: null,
          session: null,
          loading: false,
          error: null,
        });
        hasInitialRedirectedRef.current = false;
        navigate('/login-screen');
      }
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, [navigate]);

  // Remove loadUserData function as it's not needed anymore
  // const loadUserData = async (userId: string) => { ... };

  const updatePassword = async (newPassword: string) => {
    try {
      console.log('🔐 Iniciando atualização de senha');
      setState(prev => ({ ...prev, loading: true, error: null }));

      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        console.error('❌ Erro ao atualizar senha:', error);
        throw error;
      }

      console.log('✅ Senha atualizada com sucesso');
      return { success: true };
    } catch (error) {
      console.error('❌ Erro na atualização de senha:', error);
      throw error;
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const resetPassword = async (email: string) => {
    try {
      console.log('🔐 Iniciando reset de senha para:', email);
      setState(prev => ({ ...prev, loading: true, error: null }));

      // Armazenar email no sessionStorage como backup
      sessionStorage.setItem('reset_email', email);

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password#email=${encodeURIComponent(email)}`
      });

      if (error) {
        console.error('❌ Erro ao enviar email de reset:', error);
        throw error;
      }

      console.log('✅ Email de reset enviado com sucesso');
      return { success: true };
    } catch (error) {
      console.error('❌ Erro no reset de senha:', error);
      throw error;
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const login = async (credentials: LoginCredentials) => {
    try {
      console.log('🔐 Iniciando login para:', credentials.email);
      // Limpar qualquer erro anterior antes de tentar login
      setState(prev => ({ ...prev, loading: true, error: null }));

      // Step 1: Authenticate with Supabase
      console.log('1️⃣ Autenticando com Supabase...');
      const { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (error) {
        console.error('❌ Erro na autenticação Supabase:', error);
        throw error;
      }
      
      if (!data.user) {
        console.error('❌ Usuário não retornado pela autenticação');
        throw new Error('Usuário não encontrado');
      }

      console.log('✅ Autenticação Supabase bem-sucedida');

      // Step 2: Check if user exists in system tables
      console.log('2️⃣ Verificando se usuário existe no sistema...');
      const systemUser = await getSystemUser(data.user.id);
      if (!systemUser) {
        console.error('❌ Usuário não encontrado no sistema');
        throw new Error('Usuário não encontrado no sistema. Contate o administrador.');
      }

      console.log('✅ Usuário encontrado no sistema');

      // Step 3: Load complete user data (ONCE during login)
      console.log('3️⃣ Carregando dados completos do usuário...');
      
      // Carregar profile do usuário
      const profile = await getUserProfile(systemUser.id);
      if (!profile) {
        console.warn('⚠️ Perfil não encontrado, criando perfil padrão...');
        // Criar perfil padrão se não existir
        const defaultProfile = await createProfile({
          user_id: systemUser.id,
          name: systemUser.name,
          avatar_url: null,
          bio: null
        });
        console.log('✅ Perfil padrão criado:', defaultProfile);
      }

      // Carregar role do usuário
      const userTenant = await getUserRole(systemUser.id);
      if (!userTenant) {
        console.warn('⚠️ Role não encontrado, criando role padrão...');
        // Criar role padrão se não existir
        const defaultUserTenant = await upsertUserTenant({
          user_id: systemUser.id,
          role: 'student', // Role padrão
          status: 'active'
        });
        console.log('✅ Role padrão criado:', defaultUserTenant);
      }

      // Step 4: Configure complete session with all user data
      console.log('4️⃣ Configurando sessão completa...');
      const session: AuthSession = {
        user: {
          id: data.user.id,
          email: data.user.email!,
          created_at: data.user.created_at,
          updated_at: data.user.updated_at,
        },
        systemUser: systemUser, // ✅ Dados da tabela users
        profile: profile || await getUserProfile(systemUser.id), // ✅ Dados da tabela profiles
        userTenant: userTenant || await getUserRole(systemUser.id), // ✅ Dados da tabela user_tenants
        accessToken: data.session.access_token || '',
        refreshToken: data.session.refresh_token || '',
      };

      console.log('✅ Sessão completa configurada:', {
        systemUser: !!session.systemUser,
        profile: !!session.profile,
        userTenant: !!session.userTenant
      });

      // Step 5: Supabase owns session persistence; do not duplicate tokens.
      // Step 6: Update state with complete session
      console.log('6️⃣ Atualizando estado com sessão completa...');
      setState({
        user: session.user,
        session: session,
        loading: false,
        error: null,
      });
      
      console.log('✅ Login concluído com sucesso! Sessão completa carregada.');
      
      // Step 7: Redirecionamento automático - sempre para dashboard-home
      console.log('🧭 Redirecionando para dashboard-home após login');
      navigate('/dashboard-home');
      markInitialRedirected();
      
    } catch (error) {
      console.error('❌ Erro no login:', error);
      
      // Always set loading to false and show error
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Erro no login',
      }));
      
      throw error;
    }
  };

  // Função de teste para verificar acesso às tabelas
  const testTableAccess = async () => {
    console.log('🧪 Testando acesso às tabelas...');
    
    try {
      // Testar tabela users
      console.log('👤 Testando tabela users...');
      const { data: usersTest, error: usersError } = await supabase
        .from('users')
        .select('id, auth_user_id, email, name, created_at')
        .limit(1);
      console.log('✅ Tabela users acessível:', { data: usersTest, error: usersError });
      
      // Testar tabela profiles
      console.log('📋 Testando tabela profiles...');
      const { data: profilesTest, error: profilesError } = await supabase
        .from('profiles')
        .select('id, user_id, name, created_at')
        .limit(1);
      console.log('✅ Tabela profiles acessível:', { data: profilesTest, error: profilesError });
      
      // Testar tabela user_tenants
      console.log('🔗 Testando tabela user_tenants...');
      const { data: userTenantsTest, error: userTenantsError } = await supabase
        .from('user_tenants')
        .select('id, user_id, tenant_id, role, status, created_at')
        .limit(1);
      console.log('✅ Tabela user_tenants acessível:', { data: userTenantsTest, error: userTenantsError });
      
      // Testar tabela tenants
      console.log('🏢 Testando tabela tenants...');
      const { data: tenantsTest, error: tenantsError } = await supabase
        .from('tenants')
        .select('id, slug, name')
        .limit(5);
      console.log('✅ Tabela tenants acessível:', { data: tenantsTest, error: tenantsError });
      
      // Verificar se há dados nas tabelas
      console.log('📊 Verificando dados existentes...');
      const { count: usersCount } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true });
      console.log('👥 Total de usuários na tabela users:', usersCount);
      
      const { count: profilesCount } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });
      console.log('📋 Total de perfis na tabela profiles:', profilesCount);
      
      const { count: userTenantsCount } = await supabase
        .from('user_tenants')
        .select('*', { count: 'exact', head: true });
      console.log('🔗 Total de relacionamentos na tabela user_tenants:', userTenantsCount);
      
      const { count: tenantsCount } = await supabase
        .from('tenants')
        .select('*', { count: 'exact', head: true });
      console.log('🏢 Total de tenants na tabela tenants:', tenantsCount);
      
      // Mostrar detalhes dos tenants existentes
      if (tenantsTest && tenantsTest.length > 0) {
        console.log('🔍 Detalhes dos tenants existentes:');
        tenantsTest.forEach((tenant, index) => {
          console.log(`  ${index + 1}. ID: ${tenant.id}, Slug: ${tenant.slug}, Nome: ${tenant.name}`);
        });
      } else {
        console.log('⚠️ Nenhum tenant encontrado na tabela tenants');
      }
      
    } catch (error) {
      console.error('❌ Erro ao testar tabelas:', error);
    }
  };

  const register = async (data: RegistrationData): Promise<void> => {
    console.log('🚀 Iniciando registro de usuário:', data.email);
    console.log('📝 Dados recebidos:', { email: data.email, name: data.name });
    
    // Testar acesso às tabelas primeiro
    await testTableAccess();
    
    let authData: any = null;
    
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      
      // 1. Criar usuário no Supabase Auth
      console.log('📝 1. Criando usuário no Supabase Auth...');
      const { data: authResult, error: authError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            name: data.name,
          }
        }
      });

      console.log('🔍 Resultado do signUp:', { authResult, authError });

      if (authError) {
        console.error('❌ Erro no Supabase Auth:', authError);
        throw new Error(`Erro na autenticação: ${authError.message}`);
      }

      if (!authResult.user) {
        console.error('❌ Nenhum usuário retornado do Supabase Auth');
        throw new Error('Falha na criação do usuário de autenticação');
      }

      authData = authResult;
      console.log('✅ 2. Usuário criado no Supabase Auth:', authData.user.id);

      // 2. Obter tenant_id real
      console.log('🏢 3. Obtendo tenant_id...');
      let tenantId: string;
      
      try {
        tenantId = await getTenantId();
        console.log('🔍 Tenant ID retornado:', tenantId, 'Tipo:', typeof tenantId);
        
        if (!tenantId) {
          console.error('❌ Falha ao obter tenant_id');
          throw new Error('Falha ao obter tenant_id');
        }
      } catch (tenantError) {
        console.error('❌ Erro ao obter tenant_id:', tenantError);
        throw new Error(`Falha ao obter tenant_id: ${tenantError instanceof Error ? tenantError.message : 'Erro desconhecido'}`);
      }
      
      console.log('✅ Tenant ID obtido:', tenantId);

      // 3. Inserir na tabela users
      console.log('👤 4. Inserindo na tabela users...');
      const userInsertData = {
        auth_user_id: authData.user.id,
        email: data.email,
        name: data.name,
        created_at: new Date().toISOString()
      };
      console.log('🔍 Dados para inserção na tabela users:', userInsertData);
      
      const { data: userData, error: userError } = await supabase
        .from('users')
        .insert(userInsertData)
        .select()
        .single();

      console.log('🔍 Resultado da inserção na tabela users:', { userData, userError });

      if (userError) {
        console.error('❌ Erro ao inserir na tabela users:', userError);
        throw new Error(`Falha na criação do usuário: ${userError.message}`);
      }

      console.log('✅ Usuário inserido na tabela users:', userData.id);

      // 4. Inserir na tabela profiles
      console.log('📋 5. Inserindo na tabela profiles...');
      const profileInsertData = {
        user_id: userData.id,
        name: data.name,
        created_at: new Date().toISOString()
      };
      console.log('🔍 Dados para inserção na tabela profiles:', profileInsertData);
      
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .insert(profileInsertData)
        .select()
        .single();

      console.log('🔍 Resultado da inserção na tabela profiles:', { profileData, profileError });

      if (profileError) {
        console.error('❌ Erro ao inserir na tabela profiles:', profileError);
        throw new Error(`Falha na criação do perfil: ${profileError.message}`);
      }

      console.log('✅ Perfil inserido na tabela profiles');

      // 5. Inserir na tabela user_tenants
      console.log('🔗 6. Inserindo na tabela user_tenants...');
      const userTenantInsertData = {
        user_id: userData.id,
        tenant_id: tenantId,
        role: 'student',
        status: 'active'
      };
      console.log('🔍 Dados para inserção na tabela user_tenants:', userTenantInsertData);
      
      const { error: userTenantError } = await supabase
        .from('user_tenants')
        .insert(userTenantInsertData);

      console.log('🔍 Resultado da inserção na tabela user_tenants:', { userTenantError });

      if (userTenantError) {
        console.error('❌ Erro ao inserir na tabela user_tenants:', userTenantError);
        throw new Error(`Falha na criação do relacionamento usuário-tenant: ${userTenantError.message}`);
      }

      console.log('✅ Relacionamento usuário-tenant criado');

      // 6. Configurar sessão completa
      console.log('🎯 7. Configurando sessão completa...');
      const session: AuthSession = {
        user: {
          id: authData.user.id,
          email: authData.user.email!,
          created_at: authData.user.created_at,
          updated_at: authData.user.updated_at,
        },
        systemUser: userData,
        profile: profileData,
        userTenant: { 
          id: '', // Será preenchido pelo banco
          user_id: userData.id, 
          tenant_id: tenantId, 
          role: 'student', 
          status: 'active',
          created_at: new Date().toISOString()
        },
        accessToken: authData.session?.access_token || '',
        refreshToken: authData.session?.refresh_token || '',
      };

      // 7. Supabase owns session persistence; do not duplicate tokens.
      // 8. Atualizar estado
      setState({
        user: session.user,
        session: session,
        loading: false,
        error: null,
      });

      console.log('🎉 Registro concluído com sucesso!');
      console.log('🎯 Sessão configurada:', session);
      
      // 9. Redirecionamento automático - sempre para dashboard-home
      console.log('🧭 Redirecionando para dashboard-home após registro');
      navigate('/dashboard-home');
      markInitialRedirected();
      
    } catch (error) {
      console.error('❌ Erro no registro:', error);
      console.error('🔍 Detalhes do erro:', {
        name: error instanceof Error ? error.name : 'N/A',
        message: error instanceof Error ? error.message : 'N/A',
        stack: error instanceof Error ? error.stack : 'N/A'
      });
      
      // Rollback: deletar usuário do Supabase Auth se algo falhou
      if (authData?.user) {
        console.log('🔄 Fazendo rollback: deletando usuário do Supabase Auth...');
        try {
          await supabase.auth.admin.deleteUser(authData.user.id);
          console.log('✅ Rollback concluído');
        } catch (rollbackError) {
          console.error('⚠️ Erro no rollback:', rollbackError);
        }
      }
      
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido no registro',
      }));
      
      throw error; // Re-throw para o componente tratar
    }
  };

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      clearLegacyTokenStorage();

      setState({
        user: null,
        session: null,
        loading: false,
        error: null,
      });
      hasInitialRedirectedRef.current = false;
    } catch (error) {
      console.error('Logout error:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Erro no logout',
      }));
    }
  };

  const refreshSession = async () => {
    try {
      console.log('🔄 Tentando renovar sessão...');
      
      // Usar o storage correto do Supabase
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error('❌ Erro ao renovar sessão:', error);
        throw error;
      }
      
      if (data.session) {
        console.log('✅ Sessão renovada com sucesso');
        
        // Atualizar estado com nova sessão
        setState(prev => ({
          ...prev,
          user: prev.user ? {
            ...prev.user,
            accessToken: data.session.access_token || '',
            refreshToken: data.session.refresh_token || '',
          } : null,
          session: prev.session ? {
            ...prev.session,
            accessToken: data.session.access_token || '',
            refreshToken: data.session.refresh_token || '',
          } : null
        }));
      } else {
        console.log('⚠️ Nenhuma sessão retornada na renovação');
      }
    } catch (error) {
      console.error('❌ Erro ao renovar sessão:', error);
      // Não limpar a sessão por erro de renovação
      // Apenas logar o erro para debug
    }
  };

  const hasRole = (roles: string | string[]): boolean => {
    if (!state.user || !state.session?.userTenant) return false;
    
    const userRole = state.session.userTenant.role;
    const requiredRoles = Array.isArray(roles) ? roles : [roles];
    
    return requiredRoles.includes(userRole);
  };

  // Ensure all functions are defined before providing context
  const contextValue: AuthContextType = {
    user: state.user,
    session: state.session,
    loading: state.loading,
    error: state.error,
    login: login || (() => Promise.reject(new Error('Login function not available'))),
    register: register || (() => Promise.reject(new Error('Register function not available'))),
    logout: logout || (() => Promise.reject(new Error('Logout function not available'))),
    resetPassword: resetPassword || (() => Promise.reject(new Error('Reset password function not available'))),
    updatePassword: updatePassword || (() => Promise.reject(new Error('Update password function not available'))),
    refreshSession: refreshSession || (() => Promise.reject(new Error('Refresh function not available'))),
    hasRole: hasRole || (() => false),
  };
  
  console.log('📝 ContextValue criado:', contextValue);
  console.log('📝 Estado atual da sessão:', state.user);
  console.log('🔄 AuthProvider renderizando children...');

  return React.createElement(
    AuthContext.Provider,
    { value: contextValue },
    children
  );
};
